// Liga sessão, perfil e rota às telas.
import { useEffect, useState } from 'react';
import type { Plano } from '@truco6/planos';
import { config } from './comum/config.ts';
import { ligaNoAr } from './comum/liga.ts';
import { conta, type PerfilConta, type Sessao } from './comum/conta.ts';
import { api } from './comum/api.ts';
import { esquecerOrigem, guardarDestino, tirarDestino } from './comum/chegada.ts';
import { JA_TEM_CONTA, erroDaVolta } from './comum/erros-conta.ts';
import { aceitarIndicacao, guardarIndicacao } from './comum/indicacao.ts';
import { amizadeDaOrigem } from './comum/amigos.ts';
import { AvisoGlobal, avisarNaTela } from './comum/aviso.tsx';
import { lerMeuCelular, temPedidoDeCelular, tirarPedidoDeCelular } from './comum/celular.ts';
import { FolhaCelular } from './comum/FolhaCelular.tsx';
import { iniciarPresenca, usePresenca } from './comum/presenca.ts';
import { iniciarLimite, irParaMesa } from './comum/limite.ts';
import { marcarContaNova } from './comum/guia-do-dia.ts';
import { PlanoDaCasca } from './comum/SeloGratis.tsx';
import { temNavegacao, temTrilho } from './comum/abas.ts';
import { Navegacao, useComputador } from './comum/Navegacao.tsx';
import { Notificacoes } from './telas/Notificacoes.tsx';
import { Amigos } from './telas/Amigos.tsx';
import { Configuracoes } from './telas/Configuracoes.tsx';
import { PerfilGlobal, PerfilPublicoTela } from './telas/PerfilPublico.tsx';
import { definirSessaoDasRelacoes } from './comum/relacoes.ts';
import { paginaVista } from './comum/medicao.ts';
import { ir, useRota, type Rota } from './comum/rotas.ts';
import { Assinar } from './telas/Assinar.tsx';
import { AssinaturaOk } from './telas/AssinaturaOk.tsx';
import { Convite } from './telas/Convite.tsx';
import { CriarSala } from './telas/CriarSala.tsx';
import { CriarPerfil } from './telas/CriarPerfil.tsx';
import { Entrada } from './telas/Entrada.tsx';
import { Indique } from './telas/Indique.tsx';
import { ComoJogar } from './telas/ComoJogar.tsx';
import { JaTemConta } from './telas/JaTemConta.tsx';
import { GuardarConta, voltouDaMigracao } from './telas/GuardarConta.tsx';
import { Inicio } from './telas/Inicio.tsx';
import { Privacidade, Termos } from './telas/Legal.tsx';
import { Limite } from './telas/Limite.tsx';
import { MeuPlano } from './telas/MeuPlano.tsx';
import { PerfilTela } from './telas/PerfilTela.tsx';
import { TrocarApelido } from './telas/TrocarApelido.tsx';
import { Planos } from './telas/Planos.tsx';
import { Sala } from './telas/Sala.tsx';
import { Assistir } from './telas/Assistir.tsx';
import { Simulado } from './telas/Simulado.tsx';
import { CompraOk, CompraSimulada } from './telas/CompraTentos.tsx';
import { ExtratoTentos } from './telas/ExtratoTentos.tsx';
import { LojaTentos } from './telas/LojaTentos.tsx';
import { BuscaLiga } from './liga/BuscaLiga.tsx';
import { RankingTela } from './liga/Ranking.tsx';
import { ConquistasTela } from './liga/badges.tsx';
import { MissoesTela } from './liga/missoes.tsx';
import { ColecaoTela } from './liga/colecao.tsx';
import { EstatisticasTela } from './telas/Estatisticas.tsx';
import { MarcadorTela, NoitesTela, PlacarAoVivoTela } from './telas/Marcador.tsx';
import { PasseTela } from './liga/passe.tsx';
import { ConviteTrio, CriarTrio, TrioTela, TriosTela } from './liga/trios.tsx';
import { ComunidadeTela, ComunidadesTela, ConviteComunidadeTela, CriarComunidadeTela, NovaMesaDaComunidade } from './liga/comunidades.tsx';
import { CampeonatoTela, CampeonatosTela, CriarCampeonatoTela } from './liga/campeonatos.tsx';
import { Marca } from '@truco6/ui/Marca';
import { Carregando, Tela } from './telas/comum.tsx';

// Erro que volta do Google na URL: sai da barra de endereço (o resto da query fica) e vira aviso em português.
function lerErroDaUrl(): { codigo: string | null; mensagem: string } | null {
  const e = erroDaVolta(location.search, location.hash);
  if (!e) return null;
  const limpar = (p: string) => {
    const q = new URLSearchParams(p);
    for (const k of ['error', 'error_code', 'error_description']) q.delete(k);
    return q.toString();
  };
  const busca = limpar(location.search), fragmento = limpar(location.hash.slice(1));
  history.replaceState(null, '', location.pathname + (busca ? '?' + busca : '') + (fragmento ? '#' + fragmento : ''));
  return e;
}

export function App() {
  const rota = useRota();
  const computador = useComputador();
  // Uma visita no GA4 por tela, com o endereço limpo (sem código de sala ou de convite).
  const endereco = location.pathname + location.search;
  useEffect(() => paginaVista(rota.tela), [endereco]);
  const [sessao, setSessao] = useState<Sessao | null | undefined>(undefined);
  const [perfil, setPerfil] = useState<{ id: string; valor: PerfilConta | null } | null>(null);
  const [falhou, setFalhou] = useState(false);
  // O link da sala com ?c=CODIGO: a indicação de quem compartilhou vale para a conta nova (+5 partidas para ele).
  useEffect(() => {
    const c = new URLSearchParams(location.search).get('c');
    if (rota.tela === 'sala' && c && /^[A-Za-z2-9]{6}$/.test(c)) guardarIndicacao(c);
  }, []);
  const [erroDaUrl] = useState(lerErroDaUrl);
  const [aviso, setAviso] = useState(erroDaUrl && erroDaUrl.codigo !== JA_TEM_CONTA ? erroDaUrl.mensagem : null);
  const [jaTemConta, setJaTemConta] = useState(erroDaUrl?.codigo === JA_TEM_CONTA);
  // Entrou na conta do Google: o aviso do convidado não vale mais.
  useEffect(() => { if (sessao && !sessao.anonimo) setJaTemConta(false); }, [sessao?.anonimo]);
  const [boasVindas, setBoasVindas] = useState<string | null>(null);
  // Perfil recém-criado sem destino: Planos antes do Início, uma vez. Sair de Planos encerra o primeiro acesso.
  const [primeiroAcesso, setPrimeiroAcesso] = useState(false);
  useEffect(() => { if (rota.tela !== 'planos') setPrimeiroAcesso(false); }, [rota.tela]);

  useEffect(() => {
    let vivo = true;
    void conta.atual().then((s) => { if (vivo) setSessao((a) => (a === undefined ? s : a)); });
    const tirar = conta.ouvir((s) => setSessao((a) => (a && s && a.id === s.id && a.anonimo === s.anonimo ? a : s)));
    return () => { vivo = false; tirar(); };
  }, []);

  const id = sessao?.id ?? null;
  // O botão de amizade de todas as telas usa a conta da aba.
  useEffect(() => { definirSessaoDasRelacoes(sessao ?? null); }, [sessao]);
  useEffect(() => {
    if (!sessao) return;
    let vivo = true;
    setFalhou(false);
    conta.lerPerfil(sessao).then(
      // O perfil gravado agora pelo convite vale mais que uma leitura que chegou atrasada.
      (p) => { if (vivo) setPerfil((a) => (a?.id === sessao.id && a.valor ? a : { id: sessao.id, valor: p })); },
      () => { if (vivo) setFalhou(true); },
    );
    return () => { vivo = false; };
  }, [id]);

  const valor = perfil && perfil.id === id ? perfil.valor : undefined;
  // A moderação trocou o apelido: escolher outro vem antes de qualquer tela.
  const { apelidoRevisar } = usePresenca();

  // Quem chegou pelo link de um amigo (/c/CODIGO): com conta Google e perfil, o convite vale uma vez.
  const temPerfil = !!valor;
  useEffect(() => {
    if (!sessao || sessao.anonimo || !temPerfil || !valor) return;
    let vivo = true;
    void aceitarIndicacao(sessao, valor).then((quem) => { if (vivo && quem) setBoasVindas(`Você entrou pelo convite de ${quem}. Boas-vindas à mesa!`); });
    return () => { vivo = false; };
  }, [id, sessao?.anonimo, temPerfil]);
  // Presença: com o jogo aberto e o perfil criado, avisa a API a cada 30 s (o sino e o ponto de pedidos vêm junto).
  const apelido = valor?.apelido, avatar = valor?.avatar ?? null;
  useEffect(() => {
    if (!sessao || !apelido) return;
    return iniciarPresenca(sessao, { apelido, avatar });
  }, [id, sessao?.anonimo, apelido, avatar]);
  // O limite do Grátis (selo, menu, mesa): lido ao abrir, ao voltar para a aba e na mensagem da mesa.
  useEffect(() => {
    if (!sessao || !temPerfil) return;
    return iniciarLimite(sessao);
  }, [id, sessao?.anonimo, temPerfil]);
  // Assinou em outra aba (o webhook confirmou): o /api/eu volta sem teto, e o selo, o ASSINAR e o chip da mesa somem.
  // Entrou pelo link da sala de alguém e criou a conta: vira amigo do dono da sala. Pergunta de novo a cada troca de
  // tela, porque a entrada na sala acontece depois do perfil.
  useEffect(() => {
    if (!sessao || sessao.anonimo || !temPerfil) return;
    let vivo = true;
    void amizadeDaOrigem(sessao).then((amigo) => { if (vivo && amigo) setBoasVindas((b) => b ?? `Você e ${amigo} agora são amigos.`); });
    return () => { vivo = false; };
  }, [id, sessao?.anonimo, temPerfil, rota.tela]);
  // A conta anônima de antes guardou a conta com Google: o painel conta a migração, uma vez.
  useEffect(() => {
    if (sessao && !sessao.anonimo && voltouDaMigracao()) void api(sessao, 'POST', '/medida', { tipo: 'convidado_migrado' }).catch(() => {});
  }, [id, sessao?.anonimo]);
  // Guardou a conta com Google: sem celular na conta, a folha "Falta seu celular" abre logo depois da volta, uma vez.
  const [pedirCelular, setPedirCelular] = useState(false);
  useEffect(() => {
    if (!sessao || sessao.anonimo || !temPerfil || !temPedidoDeCelular()) return;
    let vivo = true;
    lerMeuCelular(sessao).then((c) => {
      if (!vivo) return;
      tirarPedidoDeCelular();
      if (!c.celular) setPedirCelular(true);
    }, () => {});
    return () => { vivo = false; };
  }, [id, sessao?.anonimo, temPerfil]);
  const guardar = (p: PerfilConta) => id && setPerfil({ id, valor: p });
  // Quem já tinha perfil (voltou do Google, por exemplo) vai ao destino guardado antes de entrar. A origem antiga sai.
  useEffect(() => {
    if (!sessao || !temPerfil) return;
    esquecerOrigem();
    const d = tirarDestino();
    if (d) ir(d, true);
  }, [id, temPerfil]);
  const perfilCriado = (p: PerfilConta) => {
    guardar(p);
    marcarContaNova(); // o guia do dia diz "Bem-vindo à mesa!" na primeira vez que a aba Jogar abre
    const d = tirarDestino();
    if (d) ir(d, true);
    // Chegou pelo link da sala: fica na sala, que senta a pessoa no primeiro lugar livre.
    else if (rota.tela !== 'sala') { setPrimeiroAcesso(true); ir({ tela: 'planos', plano: null, ciclo: 'mensal' }, true); }
  };
  // Depois de assinar, cancelar ou desistir: o plano vigente muda sem recarregar a página.
  const mudarPlano = (plano: Plano) => id && setPerfil((a) => (a?.id === id && a.valor && a.valor.plano !== plano ? { id, valor: { ...a.valor, plano } } : a));

  let tela: React.ReactNode;
  if (rota.tela === 'indicacao') tela = <GuardarIndicacao codigo={rota.codigo} />;
  else if (rota.tela === 'termos') tela = <Termos />; // abertos para quem ainda não tem conta
  else if (rota.tela === 'privacidade') tela = <Privacidade />;
  else if (rota.tela === 'como-jogar') tela = <ComoJogar />; // as regras também abrem sem conta
  else if (sessao === undefined) tela = <Carregando />;
  // O placar ao vivo abre para qualquer um, com ou sem conta e sem perfil.
  else if (rota.tela === 'placar') tela = <PlacarAoVivoTela sessao={sessao} codigo={rota.codigo} caneta={rota.caneta} telao={rota.telao} />;
  // A página do campeonato também: quem tem o link vê a tabela; inscrever pede a conta e o perfil.
  else if (rota.tela === 'campeonato' && (!sessao || !valor)) {
    tela = sessao && valor === undefined && !falhou ? <Carregando /> : <CampeonatoTela key={rota.slug} sessao={sessao} perfil={null} slug={rota.slug} aba={rota.aba} />;
  }
  else if (rota.tela === 'sala' && !sessao) tela = <Convite codigo={rota.codigo} />;
  else if (!sessao) tela = rota.tela === 'inicio' ? <Entrada /> : <Redirecionar de={rota} />;
  // A conta anônima de antes guarda a conta com Google antes de qualquer tela. Só a sala em que ela está sentada abre
  // (termina a partida); fora dela, a própria mesa pede a conta.
  else if (sessao.anonimo && rota.tela !== 'sala') tela = <GuardarConta />;
  else if (falhou) {
    tela = (
      <Tela>
        <Marca altura={40} />
        <p className="status" role="alert">Não deu para carregar seu perfil.</p>
        <button className="btn1 largo" onClick={() => location.reload()}>TENTAR DE NOVO</button>
      </Tela>
    );
  } else if (valor === undefined) tela = <Carregando />;
  else if (valor === null) tela = <CriarPerfil sessao={sessao} aoSalvar={perfilCriado} />;
  else if (apelidoRevisar) tela = <TrocarApelido sessao={sessao} perfil={valor} aoTrocar={(apelido) => guardar({ ...valor, apelido })} />;
  else if (rota.tela === 'sala') tela = <Sala key={rota.codigo} codigo={rota.codigo} vaga={rota.vaga} sessao={sessao} perfil={valor} />;
  else if (rota.tela === 'assistir') tela = <Assistir key={(rota.codigo ?? '') + (rota.amigo ?? '')} codigo={rota.codigo} amigo={rota.amigo} sessao={sessao} perfil={valor} />;
  else if (rota.tela === 'perfil') tela = <PerfilTela sessao={sessao} perfil={valor} aoSalvar={guardar} campo={rota.campo} />;
  // No computador, o sino abre as notificações num painel por cima do Início.
  else if (rota.tela === 'notificacoes') tela = computador
    ? <><Inicio sessao={sessao} perfil={valor} aoMudarPerfil={guardar} /><Notificacoes sessao={sessao} perfil={valor} painel /></>
    : <Notificacoes sessao={sessao} perfil={valor} />;
  else if (rota.tela === 'amigos') tela = <Amigos sessao={sessao} plano={valor.plano} />;
  else if (rota.tela === 'configuracoes') tela = <Configuracoes sessao={sessao} perfil={valor} aoMudarPerfil={guardar} />;
  else if (rota.tela === 'jogador') tela = <PerfilPublicoTela key={rota.id} sessao={sessao} id={rota.id} />;
  // Liga oculta (LIGA_DA_SEMANA): /liga leva para a aba Jogar.
  else if (rota.tela === 'liga') tela = ligaNoAr() ? <BuscaLiga key={rota.trio ?? 'sozinho'} sessao={sessao} perfil={valor} trio={rota.trio} /> : <IrParaJogar />;
  else if (rota.tela === 'ranking') {
    tela = rota.aba === 'comunidades' ? <ComunidadesTela sessao={sessao} perfil={valor} />
      : rota.aba === 'campeonatos' ? <CampeonatosTela sessao={sessao} perfil={valor} />
      : <RankingTela key={rota.tipo ?? 'liga'} sessao={sessao} perfil={valor} tipoInicial={rota.tipo ?? 'jogadores'} />;
  }
  else if (rota.tela === 'novo-campeonato') tela = <CriarCampeonatoTela key={rota.comunidade ?? 'solto'} sessao={sessao} perfil={valor} comunidade={rota.comunidade ?? null} />;
  else if (rota.tela === 'campeonato') tela = <CampeonatoTela key={rota.slug} sessao={sessao} perfil={valor} slug={rota.slug} aba={rota.aba} />;
  else if (rota.tela === 'nova-comunidade') tela = <CriarComunidadeTela sessao={sessao} perfil={valor} />;
  else if (rota.tela === 'comunidade') tela = <ComunidadeTela key={rota.id} sessao={sessao} perfil={valor} id={rota.id} aba={rota.aba} />;
  else if (rota.tela === 'convite-comunidade') tela = <ConviteComunidadeTela key={rota.codigo} sessao={sessao} codigo={rota.codigo} />;
  else if (rota.tela === 'conquistas') tela = <ConquistasTela sessao={sessao} perfil={valor} badge={rota.badge} />;
  else if (rota.tela === 'missoes') tela = <MissoesTela sessao={sessao} />;
  else if (rota.tela === 'colecao') tela = <ColecaoTela sessao={sessao} perfil={valor} />;
  else if (rota.tela === 'estatisticas') tela = <EstatisticasTela sessao={sessao} />;
  else if (rota.tela === 'marcador') tela = <MarcadorTela sessao={sessao} perfil={valor} />;
  else if (rota.tela === 'noites') tela = <NoitesTela sessao={sessao} perfil={valor} />;
  else if (rota.tela === 'passe') tela = <PasseTela sessao={sessao} perfil={valor} />;
  else if (rota.tela === 'trios') tela = <TriosTela sessao={sessao} />;
  else if (rota.tela === 'novo-trio') tela = <CriarTrio key={rota.convidar ?? 'novo'} sessao={sessao} perfil={valor} convidar={rota.convidar} />;
  else if (rota.tela === 'trio') tela = <TrioTela key={rota.id} sessao={sessao} id={rota.id} />;
  else if (rota.tela === 'convite-trio') tela = <ConviteTrio key={rota.codigo} sessao={sessao} perfil={valor} codigo={rota.codigo} />;
  else if (rota.tela === 'indique') tela = <Indique sessao={sessao} />;
  else if (rota.tela === 'limite') tela = <Limite sessao={sessao} perfil={valor} />;
  else if (rota.tela === 'nova-sala') {
    tela = rota.comunidade ? <NovaMesaDaComunidade key={rota.comunidade} sessao={sessao} perfil={valor} id={rota.comunidade} />
      : <CriarSala sessao={sessao} perfil={valor} ingressoInicial={rota.ingresso} />;
  }
  else if (rota.tela === 'planos') {
    tela = <Planos sessao={sessao} perfil={valor} inicial={rota.plano} cicloInicial={rota.ciclo}
      aoComecarNoGratis={primeiroAcesso ? () => ir({ tela: 'inicio' }, true) : undefined} />;
  }
  else if (rota.tela === 'assinar') tela = <Assinar key={rota.plano} sessao={sessao} plano={rota.plano} ciclo={rota.ciclo} />;
  else if (rota.tela === 'assinatura-ok') tela = <AssinaturaOk sessao={sessao} plano={rota.plano} aoMudarPlano={mudarPlano} />;
  else if (rota.tela === 'meu-plano') tela = <MeuPlano sessao={sessao} aoMudarPlano={mudarPlano} />;
  else if (rota.tela === 'simulado' && config.authLocal) tela = <Simulado sessao={rota.sessao} fatura={rota.fatura} />;
  else if (rota.tela === 'loja') tela = <LojaTentos sessao={sessao} />;
  else if (rota.tela === 'extrato') tela = <ExtratoTentos sessao={sessao} />;
  else if (rota.tela === 'compra-ok') tela = <CompraOk sessao={sessao} idSessao={rota.sessao} />;
  else if (rota.tela === 'compra-simulada' && config.authLocal) tela = <CompraSimulada idSessao={rota.sessao} />;
  else tela = <Inicio sessao={sessao} perfil={valor} aoMudarPerfil={guardar} aba={rota.tela === 'inicio' ? rota.aba : undefined} />;

  // A navegação, com a conta e o perfil prontos: no celular, só nas 4 telas principais; no computador, o trilho em toda
  // tela fora da sala e da mesa.
  const comNavegacao = !!sessao && !!valor && (temNavegacao(rota.tela) || (computador && temTrilho(rota.tela)));
  return (
    <PlanoDaCasca.Provider value={valor?.plano ?? null}>
      {comNavegacao ? <div className="com-navegacao">{tela}<Navegacao tela={rota.tela} /></div> : tela}
      {sessao && valor && <FaixaDoChamado rota={rota} />}
      {sessao && <PerfilGlobal />}
      <AvisoGlobal />
      {jaTemConta && <JaTemConta aoFechar={() => setJaTemConta(false)} />}
      {pedirCelular && sessao && !sessao.anonimo && (
        <FolhaCelular sessao={sessao} botao="SALVAR" aoFechar={() => setPedirCelular(false)}
          aoSalvar={() => { setPedirCelular(false); avisarNaTela('Celular salvo.'); }} />
      )}
      {aviso && (
        <div className="faixa" role="alert">
          <span>{aviso}</span>
          <button aria-label="Fechar aviso" onClick={() => setAviso(null)}>×</button>
        </div>
      )}
      {boasVindas && !aviso && (
        <div className="faixa boa" role="status">
          <span>{boasVindas}</span>
          <button aria-label="Fechar aviso" onClick={() => setBoasVindas(null)}>×</button>
        </div>
      )}
    </PlanoDaCasca.Provider>
  );
}

// Chamado para a mesa com o app aberto: a faixa no topo, com Entrar. Some ao fechar, ao entrar ou já na sala.
const CHAVE_CHAMADOS = 'truco6:chamados-vistos';
function chamadosVistos(): string[] {
  try { return JSON.parse(sessionStorage.getItem(CHAVE_CHAMADOS) ?? '[]') as string[]; } catch { return []; }
}
function FaixaDoChamado({ rota }: { rota: Rota }) {
  const { chamado } = usePresenca();
  const [vistos, setVistos] = useState(chamadosVistos);
  if (!chamado || vistos.includes(chamado.id) || (rota.tela === 'sala' && rota.codigo === chamado.codigo)) return null;
  const visto = () => {
    const v = [...vistos, chamado.id].slice(-20);
    setVistos(v);
    try { sessionStorage.setItem(CHAVE_CHAMADOS, JSON.stringify(v)); } catch { /* sem armazenamento: some só nesta tela */ }
  };
  return (
    <div className="faixa chamado" role="alert">
      <span><b>{chamado.de}</b> chamou você para a <b>{chamado.nome}</b></span>
      <button className="btn2 entrar" onClick={() => { visto(); irParaMesa({ tela: 'sala', codigo: chamado.codigo, vaga: 'amigo' }); }}>Entrar</button>
      <button aria-label="Fechar aviso" onClick={visto}>×</button>
    </div>
  );
}

// Guarda o código do amigo e segue para o início (entrada, se ainda não tem conta).
function GuardarIndicacao({ codigo }: { codigo: string }) {
  useEffect(() => { guardarIndicacao(codigo); ir({ tela: 'inicio' }, true); }, [codigo]);
  return null;
}

// A tela da liga com a Liga da Semana oculta: vai para a aba Jogar, sem ficar no histórico.
function IrParaJogar() {
  useEffect(() => { ir({ tela: 'inicio' }, true); }, []);
  return null;
}

// Sem conta, a tela pedida espera na Entrada: o destino fica guardado e volta depois do login.
function Redirecionar({ de }: { de: Rota }) {
  useEffect(() => { guardarDestino(de); ir({ tela: 'inicio' }, true); }, []);
  return null;
}
